import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface NavDropdownItem {
  label: string
  href: string
  description?: string
}

interface Props {
  label: string
  items: NavDropdownItem[]
  /** Optional "view all" style link rendered below a divider. */
  footer?: { label: string; href: string }
}

/**
 * Desktop nav dropdown for the dark sticky header. Follows
 * docs/conventions/frontend.md §6: items navigate via <Link> (client-side, no
 * reload), the hover gap between trigger and panel is bridged with padding on
 * the panel (pt-2, not margin), and panel text colors are explicit because the
 * navbar surface is dark.
 */
export function NavDropdown({ label, items, footer }: Props) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  // Close when the route changes (a click on any item navigates).
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Close on outside click and Escape while open.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const active = items.some((item) => pathname === item.href || pathname.startsWith(`${item.href}/`))

  return (
    <div ref={containerRef} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex items-center gap-1 text-sm transition',
          active || open ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
        )}
      >
        {label}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')} />
      </button>

      {/* pt-2 (padding, not margin) keeps the cursor inside the hover area in transit */}
      <div className={cn('absolute top-full left-1/2 z-50 -translate-x-1/2 pt-2', !open && 'hidden')}>
        <div className="w-72 rounded-xl border border-gray-800 bg-gray-950 p-2 shadow-xl shadow-black/50">
          {items.map((item) => (
            <Link key={item.href} to={item.href} className="block rounded-lg px-3 py-2.5 transition hover:bg-gray-900">
              <span className="block text-sm font-medium text-gray-100">{item.label}</span>
              {item.description && <span className="mt-0.5 block text-xs text-gray-400">{item.description}</span>}
            </Link>
          ))}
          {footer && (
            <Link
              to={footer.href}
              className="mt-1 block rounded-lg border-t border-gray-800 px-3 pt-3 pb-2.5 text-sm font-medium text-gray-300 transition hover:bg-gray-900 hover:text-gray-100"
            >
              {footer.label}
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
